import { DISPOSAL_REASONS } from '@felix/domain';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Group,
  Modal,
  NumberInput,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Textarea,
  TextInput,
} from '@mantine/core';
import { IconAlertCircle, IconAlertTriangle, IconInfoCircle } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Controller,
  type FieldValues,
  type Resolver,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { formatDay } from '../../format.js';
import { DayInput } from '../../inputs/DayInput.js';
import { useUnsavedChanges } from '../../shell-context.js';
import { formatQuantity, formatUnit } from '../materials/inventory.js';
import classes from '../materials/Materials.module.css';
import { errorCode } from '../personnel/source.js';
import {
  type CountFormValues,
  countSchema,
  type DisposalFormValues,
  disposalSchema,
  emptyDisposalForm,
  emptyIssueForm,
  emptyReceiptForm,
  type IssueFormValues,
  issueSchema,
  type LevelsFormValues,
  levelsFormOf,
  levelsSchema,
  type ReceiptFormValues,
  receiptSchema,
  toCountInput,
  toDisposalInput,
  toIssueInput,
  toLevelsPatch,
  toReceiptInput,
} from './forms.js';
import css from './Provisions.module.css';
import type { ProvisionEditor, ProvisionSource } from './source.js';
import type { ProvisionItem, ProvisionLot } from './types.js';
import { fefoDeviation, fefoPreview, issuableLots, lotExpired, unassignedBalance } from './view.js';

/**
 * Kurala bağlı şema (seçilen kalemin izlemesi, lotlar) form açıkken değişir: çözücü her
 * doğrulamada güncel şemayı kullanır.
 */
function useLiveResolver<T extends FieldValues>(schema: Parameters<typeof zodResolver>[0]) {
  const current = useRef(schema);
  current.current = schema;
  return useCallback<Resolver<T>>(
    (values, context, options) =>
      (zodResolver(current.current) as unknown as Resolver<T>)(values, context, options),
    [],
  );
}

/** Kumanya hata kodunun kullanıcı metni (en+tr); bilinmeyen kod genel hata. */
export function useProvisionError() {
  const { t } = useTranslation();
  return (code: string) =>
    t(`provisions.errors.${code}`, { defaultValue: t('materials.errors.GENERIC') });
}

/** Kalem seçicinin etiketi: kod · ad · özellik. */
export function itemLabel(item: Pick<ProvisionItem, 'itemCode' | 'name' | 'specification'>) {
  return [item.itemCode, item.name, item.specification].filter(Boolean).join(' · ');
}

/**
 * Sunucuda aranan kalem seçicisi: yazdıkça (250 ms) kumanya listesinden 30 kalem gelir. Seçilen
 * kalem listede kalır.
 */
function useItemSearch(
  source: ProvisionSource,
  vesselId: string | null,
  stockOnly: boolean,
  opened: boolean,
) {
  const [search, setSearch] = useState('');
  const [found, setFound] = useState<ProvisionItem[]>([]);
  useEffect(() => {
    if (!opened) return;
    let alive = true;
    const timer = setTimeout(() => {
      source
        .items(
          { ...(search.trim() ? { q: search.trim() } : {}), pageSize: 30, stockOnly },
          vesselId,
        )
        .then((list) => {
          if (alive) setFound(list.items.filter((i) => i.active || i.stockItemId));
        })
        .catch(() => {
          if (alive) setFound([]);
        });
    }, 250);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [source, vesselId, stockOnly, search, opened]);
  return { search, setSearch, found };
}

/** Lot seçeneğinin etiketi: batch · son kullanma · bakiye. */
function useLotLabel() {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  return (lot: ProvisionLot) =>
    t('provisions.dialogs.lotOption', {
      batch: lot.batchNo ?? t('provisions.dialogs.noBatch'),
      expiry: lot.expiryDate ? formatDay(lot.expiryDate) : t('provisions.expiryState.none'),
      quantity: `${formatQuantity(lot.balance, locale)} ${formatUnit(lot.unit)}`,
    });
}

/** Kalemin lotları (bakiyesi olanlar); kalem değişince yeniden yüklenir. */
function useItemLots(
  source: ProvisionSource,
  vesselId: string | null,
  stockItemId: string | null,
  opened: boolean,
) {
  const [lots, setLots] = useState<ProvisionLot[] | null>(null);
  useEffect(() => {
    if (!opened || !stockItemId) {
      setLots(null);
      return;
    }
    let alive = true;
    setLots(null);
    source
      .lots({ stockItemId }, vesselId)
      .then((loaded) => {
        if (alive) setLots(loaded);
      })
      .catch(() => {
        if (alive) setLots([]);
      });
    return () => {
      alive = false;
    };
  }, [source, vesselId, stockItemId, opened]);
  return lots;
}

interface DialogBase {
  opened: boolean;
  source: ProvisionSource;
  editor: ProvisionEditor;
  vesselId: string | null;
  /** Gemi yerel günü. */
  today: string;
  onClose: () => void;
  /** Kaydedildi: ekranın bildirimi. */
  onSaved: (message: string) => void;
}

function ErrorAlert({ code }: { code: string | null }) {
  const errorText = useProvisionError();
  return code ? (
    <Alert color="red" icon={<IconAlertCircle size={18} />} role="alert">
      {errorText(code)}
    </Alert>
  ) : null;
}

function Actions({ onClose, busy, save }: { onClose: () => void; busy: boolean; save: string }) {
  const { t } = useTranslation();
  return (
    <Group justify="flex-end" gap="sm">
      <Button variant="default" onClick={onClose}>
        {t('provisions.actions.cancel')}
      </Button>
      <Button type="submit" loading={busy}>
        {save}
      </Button>
    </Group>
  );
}

// ---------------------------------------------------------------------------------------------
// Stock In
// ---------------------------------------------------------------------------------------------

/**
 * Stok girişi (ADR 0099 §6): kumanya kaleminin her girişi lot açar; batch ve son kullanma kalemin
 * izleme kuralına göre zorunlu. Gemide açılmamış katalog kalemi de girilebilir (stok kalemi açılır).
 */
export function ReceiptDialog(props: DialogBase & { item: ProvisionItem | null }) {
  const { opened, source, editor, vesselId, today, item, onClose, onSaved } = props;
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`provisions.dialogs.${key}`, options);
  const [error, setError] = useState<string | null>(null);
  const { setSearch, found } = useItemSearch(source, vesselId, false, opened && !item);
  const [picked, setPicked] = useState<ProvisionItem | null>(item);
  const selected = item ?? picked;
  const schema = useMemo(() => receiptSchema(today, selected), [today, selected]);
  const form = useForm<ReceiptFormValues>({
    resolver: useLiveResolver<ReceiptFormValues>(schema),
    defaultValues: emptyReceiptForm(today, item?.materialId ?? ''),
  });
  const { errors, isSubmitting, isDirty } = form.formState;
  useUnsavedChanges(opened && isDirty);
  useEffect(() => {
    if (!opened) return;
    setError(null);
    setPicked(item);
    form.reset(emptyReceiptForm(today, item?.materialId ?? ''));
  }, [opened, item, today, form]);
  const fieldError = (key: keyof ReceiptFormValues) => {
    const message = errors[key]?.message;
    return message ? t(message) : undefined;
  };
  const options = [...(selected ? [selected] : []), ...found.filter((i) => i !== selected)]
    .filter((i, index, list) => list.findIndex((x) => x.materialId === i.materialId) === index)
    .map((i) => ({ value: i.materialId, label: itemLabel(i) }));
  const unit = formatUnit(selected?.unit ?? '');
  const tracking = (mode: string | null) =>
    t(`provisions.tracking.${mode ?? 'No'}`, { defaultValue: mode ?? '—' });

  const submit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      await editor.receive(toReceiptInput(values, today));
      onSaved(
        t('provisions.notices.received', {
          name: selected?.name ?? '',
          quantity: `${formatQuantity(Number(values.quantity), locale)} ${unit}`,
        }),
      );
    } catch (failure) {
      setError(errorCode(failure));
    }
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={k('receiptTitle')}
      closeButtonProps={{ 'aria-label': k('close') }}
      size="lg"
    >
      <form onSubmit={submit} noValidate>
        <Stack gap="sm">
          <ErrorAlert code={error} />
          <Controller
            control={form.control}
            name="materialId"
            render={({ field }) => (
              <Select
                label={k('item')}
                required
                searchable
                readOnly={item !== null}
                data={options}
                filter={({ options: all }) => all}
                onSearchChange={(value) => {
                  if (!item) setSearch(value);
                }}
                nothingFoundMessage={k('noItem')}
                {...(item ? {} : { 'data-autofocus': true })}
                description={
                  selected
                    ? k('trackingHint', {
                        batch: tracking(selected.batchTracking),
                        expiry: tracking(selected.expiryTracking),
                        onHand: `${formatQuantity(selected.onHand, locale)} ${unit}`,
                      })
                    : undefined
                }
                inputWrapperOrder={['label', 'input', 'description', 'error']}
                value={field.value || null}
                onChange={(value) => {
                  field.onChange(value ?? '');
                  setPicked(found.find((i) => i.materialId === value) ?? null);
                }}
                error={fieldError('materialId')}
              />
            )}
          />
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <Controller
              control={form.control}
              name="quantity"
              render={({ field }) => (
                <NumberInput
                  label={t('provisions.forms.quantity')}
                  required
                  min={0}
                  decimalScale={3}
                  rightSection={<span className={classes.unitSuffix}>{unit}</span>}
                  rightSectionWidth={52}
                  {...(item ? { 'data-autofocus': true } : {})}
                  value={field.value}
                  onChange={(value) => field.onChange(typeof value === 'number' ? value : '')}
                  error={fieldError('quantity')}
                />
              )}
            />
            <Controller
              control={form.control}
              name="receivedDate"
              render={({ field }) => (
                <DayInput
                  label={t('provisions.forms.receivedDate')}
                  required
                  maxDate={today}
                  value={field.value}
                  onChange={(value) => field.onChange(value ?? '')}
                  onBlur={field.onBlur}
                  error={fieldError('receivedDate')}
                />
              )}
            />
            <TextInput
              label={t('provisions.forms.batchNo')}
              required={selected?.batchTracking === 'Yes'}
              {...form.register('batchNo')}
              error={fieldError('batchNo')}
            />
            <Controller
              control={form.control}
              name="expiryDate"
              render={({ field }) => (
                <DayInput
                  label={t('provisions.forms.expiryDate')}
                  required={selected?.expiryTracking === 'Yes'}
                  minDate={today}
                  clearable
                  value={field.value}
                  onChange={(value) => field.onChange(value ?? '')}
                  onBlur={field.onBlur}
                  error={fieldError('expiryDate')}
                />
              )}
            />
          </SimpleGrid>
          {selected && !selected.stockItemId ? (
            <TextInput
              label={t('provisions.forms.location')}
              description={k('newOnBoard')}
              inputWrapperOrder={['label', 'input', 'description', 'error']}
              {...form.register('location')}
              error={fieldError('location')}
            />
          ) : null}
          <Textarea
            label={k('note')}
            autosize
            minRows={2}
            {...form.register('reason')}
            error={fieldError('reason')}
          />
          <Alert color="info" variant="light" role="note" icon={<IconInfoCircle size={18} />}>
            {k('lotImmutable')}
          </Alert>
          <Actions onClose={onClose} busy={isSubmitting} save={k('saveReceipt')} />
        </Stack>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------------------------
// Stock Out (FEFO)
// ---------------------------------------------------------------------------------------------

/**
 * Tüketim (ADR 0099 §7): lot seçilmezse sistem FEFO ile böler ve önerisi gösterilir; başka lot
 * seçilirse ve kalem FEFO zorunluysa gerekçe istenir. Süresi geçmiş lot seçilemez (§8).
 */
export function IssueDialog(props: DialogBase & { item: ProvisionItem | null }) {
  const { opened, source, editor, vesselId, today, item, onClose, onSaved } = props;
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`provisions.dialogs.${key}`, options);
  const lotLabel = useLotLabel();
  const [error, setError] = useState<string | null>(null);
  const { setSearch, found } = useItemSearch(source, vesselId, true, opened && !item);
  const [picked, setPicked] = useState<ProvisionItem | null>(item);
  const selected = item ?? picked;
  const lots = useItemLots(source, vesselId, selected?.stockItemId ?? null, opened);
  const schema = useMemo(() => issueSchema(today, selected, lots ?? []), [today, selected, lots]);
  const form = useForm<IssueFormValues>({
    resolver: useLiveResolver<IssueFormValues>(schema),
    defaultValues: emptyIssueForm(item?.stockItemId ?? ''),
  });
  const { errors, isSubmitting, isDirty } = form.formState;
  useUnsavedChanges(opened && isDirty);
  useEffect(() => {
    if (!opened) return;
    setError(null);
    setPicked(item);
    form.reset(emptyIssueForm(item?.stockItemId ?? ''));
  }, [opened, item, form]);
  const values = form.watch();
  const fieldError = (key: keyof IssueFormValues) => {
    const message = errors[key]?.message;
    return message ? t(message) : undefined;
  };
  const unit = formatUnit(selected?.unit ?? '');
  const qty = (value: number) => `${formatQuantity(value, locale)} ${unit}`;
  const usable = issuableLots(lots ?? [], today);
  const expired = (lots ?? []).filter((lot) => lot.balance > 0 && lotExpired(lot, today));
  const unassigned = selected ? unassignedBalance(selected.onHand, lots ?? []) : 0;
  const preview =
    lots && values.lotId === '' && values.quantity !== '' && values.quantity > 0
      ? fefoPreview(lots, values.quantity, today, unassigned)
      : null;
  const deviates = values.lotId !== '' && fefoDeviation(lots ?? [], values.lotId, today);
  const byId = new Map((lots ?? []).map((lot) => [lot.id, lot]));
  const options = [...(selected ? [selected] : []), ...found]
    .filter((i) => i.stockItemId)
    .filter((i, index, list) => list.findIndex((x) => x.stockItemId === i.stockItemId) === index)
    .map((i) => ({ value: String(i.stockItemId), label: itemLabel(i) }));

  const submit = form.handleSubmit(async (formValues) => {
    setError(null);
    try {
      const result = await editor.issue(toIssueInput(formValues));
      onSaved(
        t('provisions.notices.issued', {
          name: selected?.name ?? '',
          quantity: qty(Number(formValues.quantity)),
          count: result.allocations.length,
        }),
      );
    } catch (failure) {
      setError(errorCode(failure));
    }
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={k('issueTitle')}
      closeButtonProps={{ 'aria-label': k('close') }}
      size="lg"
    >
      <form onSubmit={submit} noValidate>
        <Stack gap="sm">
          <ErrorAlert code={error} />
          <Controller
            control={form.control}
            name="stockItemId"
            render={({ field }) => (
              <Select
                label={k('item')}
                required
                searchable
                readOnly={item !== null}
                data={options}
                filter={({ options: all }) => all}
                onSearchChange={(value) => {
                  if (!item) setSearch(value);
                }}
                nothingFoundMessage={k('noItem')}
                {...(item ? {} : { 'data-autofocus': true })}
                description={selected ? k('onHand', { onHand: qty(selected.onHand) }) : undefined}
                inputWrapperOrder={['label', 'input', 'description', 'error']}
                value={field.value || null}
                onChange={(value) => {
                  field.onChange(value ?? '');
                  form.setValue('lotId', '');
                  setPicked(found.find((i) => i.stockItemId === value) ?? null);
                }}
                error={fieldError('stockItemId')}
              />
            )}
          />
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <Controller
              control={form.control}
              name="quantity"
              render={({ field }) => (
                <NumberInput
                  label={t('provisions.forms.quantity')}
                  required
                  min={0}
                  decimalScale={3}
                  rightSection={<span className={classes.unitSuffix}>{unit}</span>}
                  rightSectionWidth={52}
                  {...(item ? { 'data-autofocus': true } : {})}
                  value={field.value}
                  onChange={(value) => field.onChange(typeof value === 'number' ? value : '')}
                  error={fieldError('quantity')}
                />
              )}
            />
            <Controller
              control={form.control}
              name="lotId"
              render={({ field }) => (
                <Select
                  label={t('provisions.forms.lot')}
                  disabled={!lots}
                  data={[
                    { value: '', label: t('provisions.forms.autoFefo') },
                    ...usable.map((lot) => ({ value: lot.id, label: lotLabel(lot) })),
                  ]}
                  value={field.value}
                  allowDeselect={false}
                  onChange={(value) => field.onChange(value ?? '')}
                  error={fieldError('lotId')}
                />
              )}
            />
          </SimpleGrid>
          {selected && lots === null ? <Skeleton height={56} radius="sm" /> : null}
          {preview ? (
            <section className={css.fefo} aria-label={k('fefoTitle')}>
              <strong>{k('fefoTitle')}</strong>
              <ul>
                {preview.allocations.map((allocation) => {
                  const lot = allocation.lotId ? byId.get(allocation.lotId) : null;
                  return (
                    <li key={allocation.lotId ?? 'unassigned'}>
                      <span>
                        {lot
                          ? k('fefoLot', {
                              batch: lot.batchNo ?? k('noBatch'),
                              expiry: lot.expiryDate
                                ? formatDay(lot.expiryDate)
                                : t('provisions.expiryState.none'),
                            })
                          : k('unassigned')}
                      </span>
                      <span className={classes.number}>{qty(allocation.quantity)}</span>
                    </li>
                  );
                })}
              </ul>
              {preview.shortage > 0 ? (
                <p className={css.fefoShort} role="alert">
                  {k('shortage', { quantity: qty(preview.shortage) })}
                </p>
              ) : null}
            </section>
          ) : selected && lots && values.lotId === '' ? (
            <p className={classes.muted}>{t('provisions.hints.fefo')}</p>
          ) : null}
          {deviates ? (
            <Alert
              color="yellow"
              variant="light"
              icon={<IconAlertTriangle size={18} />}
              role="note"
            >
              {selected?.fefoRequired ? k('fefoOverride') : k('fefoOverrideFree')}
            </Alert>
          ) : null}
          {expired.length > 0 ? (
            <Alert color="red" variant="light" icon={<IconAlertCircle size={18} />} role="note">
              {t('provisions.hints.expiredBlocked')} {k('expiredLots', { count: expired.length })}
            </Alert>
          ) : null}
          <Textarea
            label={
              deviates && selected?.fefoRequired ? t('provisions.forms.fefoReason') : k('note')
            }
            required={deviates && selected?.fefoRequired === true}
            autosize
            minRows={2}
            {...form.register('reason')}
            error={fieldError('reason')}
          />
          <Actions onClose={onClose} busy={isSubmitting} save={k('saveIssue')} />
        </Stack>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------------------------
// İmha / zayi
// ---------------------------------------------------------------------------------------------

/** İmha/zayi (ADR 0099 §8): neden kodu ve açıklama; süresi geçmiş lot da seçilir. Kaptanın yetkisi. */
export function DisposalDialog(props: DialogBase & { item: ProvisionItem; lotId?: string | null }) {
  const { opened, source, editor, vesselId, item, lotId, onClose, onSaved } = props;
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`provisions.dialogs.${key}`, options);
  const lotLabel = useLotLabel();
  const [error, setError] = useState<string | null>(null);
  const lots = useItemLots(source, vesselId, item.stockItemId, opened);
  const withBalance = (lots ?? []).filter((lot) => lot.balance > 0);
  const schema = useMemo(
    () => disposalSchema(item.lotTracked && withBalance.length > 0, withBalance),
    [item.lotTracked, withBalance],
  );
  const form = useForm<DisposalFormValues>({
    resolver: useLiveResolver<DisposalFormValues>(schema),
    defaultValues: emptyDisposalForm(lotId ?? ''),
  });
  const { errors, isSubmitting, isDirty } = form.formState;
  useUnsavedChanges(opened && isDirty);
  useEffect(() => {
    if (!opened) return;
    setError(null);
    form.reset(emptyDisposalForm(lotId ?? ''));
  }, [opened, lotId, form]);
  const fieldError = (key: keyof DisposalFormValues) => {
    const message = errors[key]?.message;
    return message ? t(message) : undefined;
  };
  const unit = formatUnit(item.unit);

  const submit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      await editor.dispose(toDisposalInput(String(item.stockItemId), values));
      onSaved(
        t('provisions.notices.disposed', {
          name: item.name,
          quantity: `${formatQuantity(Number(values.quantity), locale)} ${unit}`,
        }),
      );
    } catch (failure) {
      setError(errorCode(failure));
    }
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={k('disposalTitle', { name: item.name })}
      closeButtonProps={{ 'aria-label': k('close') }}
      size="lg"
    >
      <form onSubmit={submit} noValidate>
        <Stack gap="sm">
          <ErrorAlert code={error} />
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <Controller
              control={form.control}
              name="lotId"
              render={({ field }) => (
                <Select
                  label={t('provisions.forms.lot')}
                  required={item.lotTracked && withBalance.length > 0}
                  disabled={!lots}
                  data={[
                    ...(item.lotTracked && withBalance.length > 0
                      ? []
                      : [{ value: '', label: k('unassigned') }]),
                    ...withBalance.map((lot) => ({
                      value: lot.id,
                      label: `${lotLabel(lot)}${lot.expiryState === 'expired' ? ` · ${t('provisions.expiryState.expired')}` : ''}`,
                    })),
                  ]}
                  data-autofocus
                  value={field.value || null}
                  onChange={(value) => field.onChange(value ?? '')}
                  error={fieldError('lotId')}
                />
              )}
            />
            <Controller
              control={form.control}
              name="quantity"
              render={({ field }) => (
                <NumberInput
                  label={t('provisions.forms.quantity')}
                  required
                  min={0}
                  decimalScale={3}
                  rightSection={<span className={classes.unitSuffix}>{unit}</span>}
                  rightSectionWidth={52}
                  value={field.value}
                  onChange={(value) => field.onChange(typeof value === 'number' ? value : '')}
                  error={fieldError('quantity')}
                />
              )}
            />
          </SimpleGrid>
          <Controller
            control={form.control}
            name="reasonCode"
            render={({ field }) => (
              <Select
                label={t('provisions.forms.disposalReason')}
                required
                data={DISPOSAL_REASONS.map((value) => ({
                  value,
                  label: t(`provisions.disposalReasons.${value}`),
                }))}
                value={field.value || null}
                onChange={(value) => field.onChange(value ?? '')}
                error={fieldError('reasonCode')}
              />
            )}
          />
          <Textarea
            label={t('provisions.forms.disposalNote')}
            required
            autosize
            minRows={2}
            {...form.register('reason')}
            error={fieldError('reason')}
          />
          <Alert color="info" variant="light" role="note" icon={<IconInfoCircle size={18} />}>
            {k('disposalHint')}
          </Alert>
          <Actions onClose={onClose} busy={isSubmitting} save={k('saveDisposal')} />
        </Stack>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------------------------
// Sayım
// ---------------------------------------------------------------------------------------------

/** Sayım (ADR 0099 §10): lot başına sayılan miktar; fark gerekçeli düzeltme olur. */
export function CountDialog(props: DialogBase & { item: ProvisionItem }) {
  const { opened, source, editor, vesselId, item, onClose, onSaved } = props;
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`provisions.dialogs.${key}`, options);
  const [error, setError] = useState<string | null>(null);
  const lots = useItemLots(source, vesselId, item.stockItemId, opened);
  // Satırlar lotlar gelince bir kez kurulur: her çizimde yeni dizi formu sıfırlardı
  // biome-ignore lint/correctness/useExhaustiveDependencies: etiket fonksiyonları her çizimde yeni
  const rows = useMemo(() => {
    if (!lots) return null;
    const withBalance = lots.filter((lot) => lot.balance > 0);
    const unassigned = unassignedBalance(item.onHand, lots);
    return [
      ...withBalance.map((lot) => ({
        lotId: lot.id,
        label: k('fefoLot', {
          batch: lot.batchNo ?? k('noBatch'),
          expiry: lot.expiryDate ? formatDay(lot.expiryDate) : t('provisions.expiryState.none'),
        }),
        balance: lot.balance,
      })),
      ...(unassigned > 0 || withBalance.length === 0
        ? [{ lotId: '', label: k('unassigned'), balance: unassigned }]
        : []),
    ];
  }, [lots, item.onHand]);
  const form = useForm<CountFormValues>({
    resolver: zodResolver(countSchema),
    defaultValues: { counts: [], reason: '' },
  });
  const { fields } = useFieldArray({ control: form.control, name: 'counts' });
  const { errors, isSubmitting, isDirty } = form.formState;
  useUnsavedChanges(opened && isDirty);
  useEffect(() => {
    if (!opened || !rows) return;
    setError(null);
    form.reset({
      counts: rows.map((row) => ({ lotId: row.lotId, counted: row.balance })),
      reason: '',
    });
  }, [opened, rows, form]);
  const values = form.watch();
  const unit = formatUnit(item.unit);
  const qty = (value: number) => `${formatQuantity(value, locale)} ${unit}`;

  const submit = form.handleSubmit(async (formValues) => {
    setError(null);
    try {
      const result = await editor.count(toCountInput(String(item.stockItemId), formValues));
      onSaved(
        result.adjustments.length === 0
          ? t('provisions.notices.countedSame', { name: item.name })
          : t('provisions.notices.counted', { name: item.name, count: result.adjustments.length }),
      );
    } catch (failure) {
      setError(errorCode(failure));
    }
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={k('countTitle', { name: item.name })}
      closeButtonProps={{ 'aria-label': k('close') }}
      size="lg"
    >
      <form onSubmit={submit} noValidate>
        <Stack gap="sm">
          <ErrorAlert code={error} />
          {!rows ? (
            <Skeleton height={96} radius="sm" />
          ) : (
            <div className={css.countTable}>
              <table className={classes.miniTable}>
                <thead>
                  <tr>
                    <th scope="col">{t('provisions.forms.lot')}</th>
                    <th scope="col" data-numeric>
                      {t('provisions.columns.balance')}
                    </th>
                    <th scope="col">{t('provisions.forms.counted')}</th>
                    <th scope="col" data-numeric>
                      {k('difference')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {fields.map((field, index) => {
                    const row = rows[index];
                    const counted = values.counts[index]?.counted;
                    const difference =
                      row && counted !== '' && counted !== undefined ? counted - row.balance : null;
                    const message = errors.counts?.[index]?.counted?.message;
                    return (
                      <tr key={field.id}>
                        <td>{row?.label}</td>
                        <td data-numeric>{row ? qty(row.balance) : '—'}</td>
                        <td>
                          <Controller
                            control={form.control}
                            name={`counts.${index}.counted`}
                            render={({ field: input }) => (
                              <NumberInput
                                size="xs"
                                aria-label={k('countedFor', { lot: row?.label ?? '' })}
                                min={0}
                                decimalScale={3}
                                {...(index === 0 ? { 'data-autofocus': true } : {})}
                                value={input.value}
                                onChange={(value) =>
                                  input.onChange(typeof value === 'number' ? value : '')
                                }
                                error={message ? t(message) : undefined}
                              />
                            )}
                          />
                        </td>
                        <td data-numeric>
                          {difference === null || difference === 0
                            ? '—'
                            : `${difference > 0 ? '+' : '−'}${qty(Math.abs(difference))}`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <Textarea
            label={t('provisions.forms.countReason')}
            required
            autosize
            minRows={2}
            {...form.register('reason')}
            error={errors.reason?.message ? t(errors.reason.message) : undefined}
          />
          <Actions onClose={onClose} busy={isSubmitting} save={k('saveCount')} />
        </Stack>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------------------------
// Seviye ve yer
// ---------------------------------------------------------------------------------------------

/** Stok kaleminin yeri, en az, yeniden sipariş ve en çok seviyesi (`provisions.edit`). */
export function LevelsDialog(props: Omit<DialogBase, 'source'> & { item: ProvisionItem }) {
  const { opened, editor, item, onClose, onSaved } = props;
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`provisions.dialogs.${key}`, options);
  const [error, setError] = useState<string | null>(null);
  const form = useForm<LevelsFormValues>({
    resolver: zodResolver(levelsSchema),
    defaultValues: levelsFormOf(item),
  });
  const { errors, isSubmitting, isDirty } = form.formState;
  useUnsavedChanges(opened && isDirty);
  useEffect(() => {
    if (!opened) return;
    setError(null);
    form.reset(levelsFormOf(item));
  }, [opened, item, form]);
  const fieldError = (key: keyof LevelsFormValues) => {
    const message = errors[key]?.message;
    return message ? t(message) : undefined;
  };
  const unit = formatUnit(item.unit);
  const number = (name: 'minQty' | 'reorderLevel' | 'maxQty', label: string, required = false) => (
    <Controller
      control={form.control}
      name={name}
      render={({ field }) => (
        <NumberInput
          label={label}
          required={required}
          min={0}
          decimalScale={3}
          rightSection={<span className={classes.unitSuffix}>{unit}</span>}
          rightSectionWidth={52}
          value={field.value}
          onChange={(value) => field.onChange(typeof value === 'number' ? value : '')}
          error={fieldError(name)}
        />
      )}
    />
  );

  const submit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      await editor.updateLevels(
        String(item.stockItemId),
        toLevelsPatch(item.rowVersion ?? 1, values),
      );
      onSaved(t('provisions.notices.levels', { name: item.name }));
    } catch (failure) {
      setError(errorCode(failure));
    }
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={k('levelsTitle', { name: item.name })}
      closeButtonProps={{ 'aria-label': k('close') }}
    >
      <form onSubmit={submit} noValidate>
        <Stack gap="sm">
          <ErrorAlert code={error} />
          <TextInput
            label={t('provisions.forms.location')}
            data-autofocus
            {...form.register('location')}
            error={fieldError('location')}
          />
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
            {number('minQty', t('provisions.columns.min'), true)}
            {number('reorderLevel', t('provisions.columns.reorder'))}
            {number('maxQty', t('provisions.columns.max'))}
          </SimpleGrid>
          <p className={classes.muted}>{k('levelsHint')}</p>
          <Actions onClose={onClose} busy={isSubmitting} save={t('provisions.actions.save')} />
        </Stack>
      </form>
    </Modal>
  );
}
